import React, { useEffect, useState } from 'react';
import { api, authStorage } from '../api';
import { useAdminAuth } from '../context/AdminAuthContext';
import ImageUploadField from '../components/ImageUploadField';
import { showError, showUpdateSuccess } from '../utils/adminAlerts';

const AdminProfileSettings = () => {
  const { user, refreshUser } = useAdminAuth();
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    avatarUrl: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user) return;

    setFormData({
      fullName: user.fullName || '',
      email: user.email || '',
      avatarUrl: user.avatarUrl || '',
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    });
  }, [user?.id]);

  const handleChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');

    if (formData.newPassword && formData.newPassword !== formData.confirmPassword) {
      setError('New password and confirmation do not match.');
      setSaving(false);
      return;
    }

    try {
      const previousEmail = user?.email || '';
      const response = await api.updateProfile({
        fullName: formData.fullName,
        email: formData.email.trim(),
        avatarUrl: formData.avatarUrl || null,
        currentPassword: formData.newPassword ? formData.currentPassword : null,
        newPassword: formData.newPassword || null,
      });

      const updatedUser = response.data;
      authStorage.setUser(updatedUser);
      await refreshUser?.();

      const emailChanged = previousEmail.trim().toLowerCase() !== (updatedUser.email || '').trim().toLowerCase();
      if (emailChanged) {
        setFormData((prev) => ({
          ...prev,
          email: updatedUser.email || prev.email,
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        }));
      }

      await showUpdateSuccess(emailChanged ? 'Profile and email' : 'Profile');
    } catch (err) {
      const message = err.message || 'Failed to update profile.';
      setError(message);
      await showError(message, 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl">
      <div className="mb-4">
        <h1 className="mb-1 text-2xl font-extrabold text-slate-900 dark:text-white">Profile Change</h1>
        <p className="text-sm text-slate-500 dark:text-gray-400">
          Update your profile photo, name, email, and password.
        </p>
      </div>

      <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-[#141414]">
        <form onSubmit={handleSubmit} className="space-y-4">
          <ImageUploadField
            label="Profile Photo"
            value={formData.avatarUrl}
            onChange={(url) => setFormData((prev) => ({ ...prev, avatarUrl: url }))}
          />

          <div>
            <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-gray-400">
              Full Name
            </label>
            <input
              type="text"
              value={formData.fullName}
              onChange={handleChange('fullName')}
              required
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-primary focus:outline-none dark:border-white/15 dark:bg-[#1a1a1a] dark:text-white"
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-gray-400">
              Email
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={handleChange('email')}
              required
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-primary focus:outline-none dark:border-white/15 dark:bg-[#1a1a1a] dark:text-white"
            />
            <p className="mt-1 text-xs text-slate-500 dark:text-gray-400">
              You can change this email. Next login OTP will be sent to the new address.
            </p>
          </div>

          <div className="border-t border-slate-100 pt-4 dark:border-white/10">
            <p className="mb-3 text-xs font-bold uppercase tracking-wide text-slate-400">Change Password (optional)</p>

            <p className="mb-3 text-xs text-slate-500 dark:text-gray-400">
              Leave password fields empty if you only want to update your photo, name, or email.
            </p>

            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-gray-400">
                  Current Password
                </label>
                <input
                  type="password"
                  value={formData.currentPassword}
                  onChange={handleChange('currentPassword')}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-primary focus:outline-none dark:border-white/15 dark:bg-[#1a1a1a] dark:text-white"
                />
              </div>

              <div>
                <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-gray-400">
                  New Password
                </label>
                <input
                  type="password"
                  value={formData.newPassword}
                  onChange={handleChange('newPassword')}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-primary focus:outline-none dark:border-white/15 dark:bg-[#1a1a1a] dark:text-white"
                />
              </div>

              <div>
                <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-gray-400">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  value={formData.confirmPassword}
                  onChange={handleChange('confirmPassword')}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-primary focus:outline-none dark:border-white/15 dark:bg-[#1a1a1a] dark:text-white"
                />
              </div>
            </div>
          </div>

          {error ? <p className="text-sm text-red-500 dark:text-red-400">{error}</p> : null}

          <button
            type="submit"
            disabled={saving}
            className="rounded-lg bg-primary px-4 py-2 text-sm font-bold text-black disabled:opacity-70"
          >
            {saving ? 'Saving...' : 'Update Profile'}
          </button>
        </form>
      </section>
    </div>
  );
};

export default AdminProfileSettings;
